import { useOutletContext } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useModal from 'lib/hooks/useModal';
import { useServices } from 'lib/hooks/queries/services/useServices';
import { useStepperContext } from 'lib/context/StepperContext/StepperContext';

import AgTablePage from 'components/shared/AgTablePage/AgTablePage';
import AddServicesModal from 'components/shared/AddServicesModal/AddServicesModal';
import EmptyDataRows from 'components/shared/AgTablePage/components/EmptyDataRows';
import {
  isSubstepCompleted,
  resolveContinueAction
} from 'components/shared/OnboardingWizard/lib/resolveContinueAction';

import BulkActions from './components/BulkActions';
import { scheduleOfFeesMainColumns } from './lib/helpers';
import EditServiceModal from './components/EditServiceModal';

const ScheduleOfFees = ({
  addFooterActions,
  setHeaderButtonProps,
  mutateOrchestratorData,
  onNext,
  orchestratorData,
  orchestratorLoading
}) => {
  const { practice, practiceId } = useOutletContext() || {};
  const { isAdmin } = useStepperContext();
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedRows, setSelectedRows] = useState([]);
  const gridApiRef = useRef(null);

  const columnDefs = useMemo(() => scheduleOfFeesMainColumns, []);

  const handleClearSelection = useCallback(() => {
    gridApiRef.current?.deselectAll();
    setSelectedRows([]);
  }, []);

  const handleSelectionChanged = useCallback(({ api }) => {
    gridApiRef.current = api;
    setSelectedRows(api?.getSelectedNodes() ?? []);
  }, []);

  useEffect(() => {
    setHeaderButtonProps({
      text: 'Add services',
      onClick: openModal,
      icon: 'add-square-mono'
    });

    addFooterActions([
      {
        text: 'Continue',
        onClick: resolveContinueAction({
          isDirty: false,
          isCompleted: isSubstepCompleted(orchestratorData),
          hasExistingData: true,
          onConfirm: () => mutateOrchestratorData({}),
          onNext
        }),
        isDisabled: orchestratorLoading
      }
    ]);
  }, [
    orchestratorLoading,
    orchestratorData?.handlerStatus,
    openModal,
    setHeaderButtonProps,
    addFooterActions,
    mutateOrchestratorData,
    onNext
  ]);

  const renderEmptyState = useCallback(
    () => (
      <EmptyDataRows
        title="No services added yet"
        description="Add services to define your practice's schedule of fees"
        emptyTextButton="Add services"
      />
    ),
    []
  );

  return (
    <div className="mb-4 flex flex-1 flex-col">
      <AgTablePage
        defaultColumns={columnDefs}
        useData={useServices}
        title="Services"
        name="services"
        noHeader
        rowStyle={{ cursor: 'pointer' }}
        queryParams={{ forPractice: false, getPayers: true, practiceId: Number(practiceId) }}
        queryOptions={{ refetchOnMount: 'always' }}
        ModalComponent={EditServiceModal}
        autoHeight
        className="overflow-y-auto rounded-2xl border border-solid border-neutral-200"
        noRowsOverlayComponent={renderEmptyState}
        onSelectionChanged={handleSelectionChanged}
      />

      <BulkActions selectedRows={selectedRows} onClearSelection={handleClearSelection} />

      <AddServicesModal
        isOpen={isOpen}
        onClose={closeModal}
        practice={practice}
        practiceId={Number(practiceId)}
        isAdminContext={isAdmin}
      />
    </div>
  );
};

export default ScheduleOfFees;
